(ns hai.tasks.form
  "The task form, as the pieces it is built from and two layouts of them:
   [[card-form]], filling a fixed-height card, and [[inline-form]], growing
   with its content. A new task starts as its title, and the first thing typed
   into it, or the button under it, opens the rest. Both then lay the task
   out whole, new or saved, as it reads:
   the title with the assignee beside it, over the deadline, the urgency and
   the host's stamp; ruled off beneath them, the description; then what the
   task relates to — the links and tags rows, or in the card, the Related
   drawer along its foot. The title and the description read as text and open
   for editing on a click, one at a time.

   A new task stages everything in the draft and reaches `on-save` together,
   in one payload, from Save. An existing task saves as it is edited: a click
   on a chip, a pick or a ✕ saves at once, and the title and the description,
   edited in one place at a time, save as they are typed. Mirrors
   [[hai.notes.form]]."
  (:require
   [clojure.string :as str]
   [goog.object :as gobj]
   [hai.entity.draft :as draft]
   [hai.entity.form :as entity-form]
   [hai.entity.link-combobox :as link-combobox]
   [hai.entity.lock :as lock]
   [hai.entity.sections :as sections]
   [hai.tags.combobox :as tag-combobox]
   [hai.tasks.assignee-bubble :as bubble]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.deadline-picker :as deadline-picker]
   [hai.tasks.drawer :as drawer]
   [hai.tasks.drawer.core :as drawer-core]
   [hai.tasks.urgency :as urgency]
   [hai.tasks.urgency-menu :as urgency-menu]
   [hai.ui.components :as c]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.icons :as icons]
   [hai.ui.keyboard :as keyboard]
   [hai.ui.rf-sugar :refer [<s >e >e!]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private urgency-icon (icons/mui-icon :outlined :bolt))
(def ^:private chevron-icon (icons/mui-icon :outlined :expand_more))

(defn- caret-to-end
  "Re-editing continues from the end of the text, not the start — once, as
   the editor opens. Focus coming back with the page, after a look at another
   tab, leaves the caret where the writer left it."
  [e]
  (let [el (.-target e)
        end (.. el -value -length)]
    (when-not (gobj/get el "caretPlaced")
      (gobj/set el "caretPlaced" true)
      (set! (.-selectionStart el) end)
      (set! (.-selectionEnd el) end))))

(defn- left-the-page?
  "Whether a blur came from the page losing focus — to another tab, another
   window, the devtools — rather than from something on the page taking it.
   An existing task's typed field stays open, and its lock held, through the
   first: the writer is looking something up, not done."
  []
  (not (.hasFocus js/document)))

;; ⚡ and the urgency as a pill, a chevron opening the menu that changes it.
;; None reads as an invitation (dashed); a lane reads as a chip (`.filled`).
(o/defstyled urgency-pill :span
  {:display "inline-flex" :align-items "stretch"
   :align-self "center"
   :box-sizing "border-box"
   :height "26px"
   :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
   :border-radius "7px"}
  [:button
   {:display "inline-flex" :align-items "center" :gap "4px"
    :padding "3px 6px 3px 10px"
    :border "none" :border-radius "7px"
    :background "transparent"
    :font-family "inherit"
    :font-size "12px" :font-weight "600" :line-height "1"
    :color "rgba(74,93,63,0.82)"
    :cursor "pointer"
    :transition "background 0.15s"}
   [:&:hover {:background "rgba(50,64,37,0.06)"}]
   ["&[data-state='open']" {:background "rgba(50,64,37,0.06)"}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  ;; The read-only form shows the value and nothing to press.
  [:button:disabled
   {:cursor "default"}
   [:&:hover {:background "transparent"}]]
  [:&.filled
   {:border-style "solid" :border-color t/--note-sage-55}
   [:button {:color t/--card-forest}]])

;; The prototype's lock panel: who holds a typed field this form was refused,
;; and for how long — and, when it is the writer somewhere else, the button
;; that moves their editing here.
(o/defstyled lock-panel :div
  {:position "relative"
   :width "230px"}
  [:.lock-panel-close
   {:position "absolute" :top "-4px" :right "-4px"
    :width "22px" :height "22px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "6px"
    :background t/--card-forest-06
    :color t/--card-forest-70
    :cursor "pointer"}
   [:&:hover {:background t/--card-forest-10 :color t/--card-forest}]
   [:svg {:fill "currentColor"}]]
  [:.lock-panel-head
   {:display "flex" :gap "9px" :align-items "flex-start"
    :padding-right "22px"}
   [bubble/assignee-avatar
    {:width "30px" :height "30px"
     :font-weight "800"}]]
  [:.lock-panel-who {:min-width "0"}]
  [:.lock-panel-name
   {:display "block"
    :font-size "13.5px" :font-weight "800" :line-height "1.3"
    :color t/--card-forest
    :overflow-wrap "anywhere"}]
  [:.lock-panel-sub
   {:display "block"
    :font-size "12.5px" :line-height "1.35"
    :color t/--link-muted}]
  [:.lock-panel-sentence
   {:display "block"
    :font-size "13px" :line-height "1.4"
    :color t/--card-forest
    :overflow-wrap "anywhere"}
   [:strong {:font-weight "800"}]]
  [#{:.lock-panel-held :.lock-panel-quiet}
   {:padding "9px 2px 0"
    :font-size "12.5px" :line-height "1.4"}]
  [:.lock-panel-held {:color t/--card-forest-70}]
  [:.lock-panel-quiet {:color t/--link-muted}]
  [:.lock-panel-take
   {:display "flex" :align-items "center" :justify-content "center"
    :width "100%" :height "32px"
    :margin-top "10px"
    :border "none" :border-radius "8px"
    :background t/--humble-primary :color "white"
    :font-family "inherit" :font-size "12.5px" :font-weight "800"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--humble-primary-dark}]])

(o/defstyled shell :div
  {:display "flex" :flex-direction "column"
   :padding "6px 16px 18px"}
  ;; The input chrome lives on the wrap so the → can sit inside it as a
  ;; full-height right segment that grows with the field.
  [:.title-input-wrap
   {:display "flex" :align-items "stretch"
    :position "relative"
    :background "#FFFFFF"
    :border-width "1px" :border-style "solid" :border-color t/--property-3
    :border-radius "12px"
    :overflow "hidden"
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
   [:&:focus-within {:border-color t/--card-sage
                     :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]]
  ;; A textarea so a long title wraps and grows the field downward rather
  ;; than sliding out of view.
  [:.title-input
   {:flex "1" :min-width "0"
    :padding "13px 16px"
    :background "transparent"
    :border "none"
    :outline "none" :resize "none"
    :font-family "inherit"
    :font-size "15px" :font-weight "500" :line-height "1.4"
    :color t/--card-forest
    :field-sizing "content"
    :overflow "hidden"}
   [:&::placeholder {:color t/--link-muted :font-weight "400"}]]
  ;; The title is edited as the heading it reads as: the heading's type, in
  ;; chrome that sits around the text rather than moving it.
  [:.title-input-wrap.heading
   {:margin "-6px 0 -6px -8px"
    :border-radius "8px"}
   [:.title-input
    {:padding "5px 7px"
     :font-size "17px" :font-weight "800" :line-height "1.28"}]]
  ;; The head: the title and the assignee over the chips, ruled off from the
  ;; description edge to edge, past the shell's inset.
  [:.task-head
   {:margin "0 -16px"
    :padding "6px 16px 12px"
    :border-bottom-width "1px" :border-bottom-style "solid"
    :border-bottom-color t/--property-3}]
  [:.task-title-line
   {:position "relative"
    :display "flex" :align-items "flex-start" :gap "9px"}
   [#{:.title-input-wrap :.title-display} {:flex "1 1 auto" :min-width "0"}]]
  [:.title-display
   {:margin "0"
    :font-size "17px" :font-weight "800" :line-height "1.28"
    :color t/--card-forest
    :word-break "break-word" :overflow-wrap "anywhere"}
   ;; Struck through as a completed task's row is.
   [:&.done {:opacity "0.5" :text-decoration "line-through"}]
   ;; A new task's blank title reads as the field's placeholder does.
   [:&.empty {:font-weight "400" :color t/--link-muted}]]
  ;; Centred on the title's first line.
  [:.task-assignee
   {:flex "none"
    :margin-top "-5px"}
   [bubble/assignee-avatar
    {:width "32px" :height "32px"
     :font-size "13px"}
    [:svg {:width "18px" :height "18px"}]]]
  [:.task-chip-row
   {:margin-top "9px"
    :display "flex" :flex-wrap "wrap" :align-items "center" :gap "7px"}]
  ;; Under a new task's title while that is all there is: opens the rest.
  [:.task-expand
   {:display "flex" :align-items "center" :justify-content "center"
    :width "34px" :height "24px"
    :margin "10px auto 0" :padding "0"
    :border "none" :border-radius "999px"
    :background t/--card-forest-06
    :box-shadow "inset 0 0 0 1px var(--property-3)"
    :color t/--link-muted
    :cursor "pointer"}
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]
   [:&:hover
    {:box-shadow "inset 0 0 0 1px var(--card-sage)"
     :color t/--card-forest}]]
  ;; The stamp centred in a box the assignee bubble's width, so the two share a
  ;; centre line.
  [:.task-stamp
   {:margin-left "auto"
    :width "32px"
    :display "flex" :justify-content "center"}]
  ;; A field that opens for editing says so on hover, with a fill rather than a
  ;; standing border — one that always looks like a field turns the task into a
  ;; form. Hover cues only where hover exists.
  [:.editable
   {:border-radius "7px" :cursor "text"}]
  [:at-media {:hover "hover"}
   [:.editable:hover
    {:background t/--card-forest-06
     :box-shadow "0 0 0 3px var(--card-forest-06)"}]]
  ;; The description takes whatever height the host leaves under the head, so
  ;; in a card with room to spare the actions sit at its bottom.
  [:.task-body
   {:position "relative"
    :flex "1 0 auto"
    :min-height "72px"
    :padding "12px 0"
    :display "flex" :flex-direction "column"}]
  ;; Where a field's lock panel hangs from: the right end of the title line,
  ;; and of the description's first line rather than of the whole body, which
  ;; can run the height of the card.
  [:.lock-anchor
   {:position "absolute" :right "0"
    :width "1px" :height "1px"
    :pointer-events "none"}]
  [:.task-title-line [:.lock-anchor {:bottom "0"}]]
  [:.task-body [:.lock-anchor {:top "34px"}]]
  [:.task-description
   {:flex "1 0 auto"
    :font-size "13.5px" :line-height "1.6"
    :color t/--card-forest
    :white-space "pre-wrap"
    :word-break "break-word" :overflow-wrap "anywhere"}
   [:&.empty {:color t/--link-muted :font-style "italic"}]]
  ;; While editing, the same white chrome the title input has, sitting around
  ;; the text rather than moving it, over the whole space the description
  ;; read-out took — so nothing below it moves.
  [:.detail-input-wrap
   {:flex "1 0 auto"
    :display "flex" :flex-direction "column"
    :margin "-9px -11px"}]
  [:.detail-input
   {:flex "1 0 auto"
    :display "block"
    :width "100%"
    :box-sizing "border-box"
    :padding "8px 10px"
    :outline "none" :resize "none"
    :background "#FFFFFF"
    :border-width "1px" :border-style "solid" :border-color t/--property-3
    :border-radius "8px"
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :font-family "inherit" :font-size "13.5px" :line-height "1.6"
    :color t/--card-forest
    :field-sizing "content"
    :overflow "hidden"
    :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
   [:&:focus {:border-color t/--card-sage
              :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]
   [:&::placeholder {:color t/--link-muted}]]
  ;; The links and tags rows under the description.
  [:.detail-rows
   {:display "flex" :flex-direction "column" :align-items "flex-start" :gap "10px"}
   ;; One pill height across the rows — the shared ＋ chips inherit their
   ;; notes size, so pin them up to match only inside this form.
   [sections/add-section-pill {:box-sizing "border-box" :height "24px"}]]
  ;; The card: the description takes what the drawer leaves and scrolls in it,
  ;; edge to edge so an open editor's chrome isn't clipped at its sides. The
  ;; least the drawer leaves it is the strip [[hai.tasks.drawer]] stands its …
  ;; in, which brings the description back.
  [:.task-card-layout
   {:flex "1 1 0" :min-height "0"
    :display "flex" :flex-direction "column"}
   [:.task-body
    {:flex "1 1 0" :min-height "16px"
     :margin "0 -16px"
     :padding "0 16px"
     :overflow-y "auto"
     :scrollbar-width "thin"
     :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"}
    ;; Its space above and below is content rather than padding, which a box
    ;; can't shrink under, so the drawer can cover the description down to
    ;; that strip.
    [#{:&::before :&::after} {:content "''" :flex "none" :height "12px"}]
    [:.lock-anchor {:right "16px"}]]]
  ;; The drawer spans the card and sits on its bottom edge, past the shell's
  ;; inset.
  [:.task-card-foot
   {:flex "0 1 auto" :min-height "0"
    :display "flex" :flex-direction "column"
    :margin "0 -16px -18px"}])

(defn from-task
  "A task as a draft payload: every field the composer stages. What it is
   seeded with, and what it measures having something to save against.

   `default-urgency` is the urgency a task carrying none of its own starts at —
   a new-task host passes the user's saved default
   ([[hai.tasks.data/default-urgency]]) and hands the form the same value, so
   the seed and the baseline it is measured against agree.

   `:shares` starts empty whatever the task: only a new task stages its
   shares, a saved one changes them at once."
  ([task] (from-task task nil))
  ([task default-urgency]
   {:title       (or (:task/title task) "")
    :description (or (:task/description task) "")
    :urls        (vec (keep :activity/url (:task/activities task)))
    :contacts    (vec (:task/contacts task))
    :notes       (vec (:task/notes task))
    :tasks       (vec (:task/tasks task))
    :assignee    (:task/assigned-to task)
    :urgency     (or (:task/urgency task) default-urgency urgency/none)
    :deadline    (when (:task/deadline-at task) (deadline/from-task task))
    :tags        (vec (:task/tags task))
    :spaces      (vec (:entity/spaces task))
    :shares      []}))

(defn- new-task-assignee
  "A new task's assignee bubble: the staged pick as a circle, which opens the
   host's picker (`render-assignee-picker`) to change it."
  [{:keys [assignee render-assignee-picker on-pick]}]
  (let [avatar [bubble/assignee-avatar
                {:assignee (if (data/picked-assignee? assignee)
                             (data/picked->assignee assignee)
                             assignee)
                 :me? false}]]
    (if render-assignee-picker
      (render-assignee-picker {:on-pick on-pick :trigger avatar})
      avatar)))

;;
;; The pieces. Each takes the form map [[composer]] hands its layout: the
;; host's props, the local state the pieces share, the draft, and what changes
;; and saves it.
;;

(defn- form-state
  "The local state a form's pieces share, made once per form. A new task's
   typed field open, the title as it starts, and whether it has opened past
   its title: nil until something decides."
  []
  {:!open-field (r/atom :task/title)
   :!expanded? (r/atom nil)
   :!open-sections (r/atom #{})
   :deadline-controls (deadline-picker/controls)})

(def ^:private draft-keys
  {:task/title :title
   :task/description :description})

(defn- new-task-form
  "A new task's form: the draft at `draft-id`, whether it holds anything to
   save, the `change!` and `save!` that act on it, and its typed fields, one
   open at a time. It starts as its title alone, and opens past it
   (`expanded?`) on the first thing typed or on `expand!` — or from the start,
   around a draft handed a title. Once open it stays open."
  [{:keys [!open-field !expanded?]} {:keys [draft-id task default-urgency saving? on-save]}]
  (let [{:keys [title] :as draft} @(<s [::draft/draft draft-id])
        open-field @!open-field
        ;; Measured against the task as filed, so a composer handed unsaved
        ;; contents — the universal bar's title — has something to save from
        ;; its first render.
        unfiled? (not= draft (from-task task default-urgency))
        expanded? (if-some [decided @!expanded?]
                    decided
                    (not (str/blank? title)))
        change! (fn [changes] (>e [::draft/change draft-id changes]))]
    {:existing? false
     :draft draft
     :unfiled? unfiled?
     :change! change!
     :expanded? expanded?
     ;; From the keyboard the title has closed by now, the focus having gone
     ;; to the button: it opens again and takes the focus back.
     :expand! (fn []
                (reset! !expanded? true)
                (reset! !open-field :task/title))
     :editing-title? (= :task/title open-field)
     :editing-details? (= :task/description open-field)
     :open-text! (fn [field] (reset! !open-field field))
     ;; Synchronous: a render ahead of a queued keystroke would put older
     ;; text back in the box, over what was typed since.
     :type-text! (fn [field value]
                   (when (or expanded? (seq value))
                     (reset! !expanded? true))
                   (>e! [::draft/change draft-id {(draft-keys field) value}]))
     :close-text! (fn [field]
                    (swap! !open-field #(when-not (= field %) %)))
     :save! (fn []
              (let [trimmed-title (str/trim title)]
                (when (and unfiled? (seq trimmed-title) (not saving?))
                  (on-save (assoc draft :title trimmed-title)))))}))

(defn- existing-task-form
  "An existing task's form: the task as it is, with what is being typed into
   its title and description over it; `change!` saves a click at once, and
   the typed fields open, take what is typed and close through their locks
   ([[hai.tasks.data/::type-text]]). `change-fx` rides every save."
  [{:keys [task change-fx]}]
  (let [task-uuid (:task/uuid task)
        ident (data/task-ident task-uuid)
        typed-title @(<s [::draft/draft (data/text-draft-id task-uuid :task/title)])
        typed-description @(<s [::draft/draft (data/text-draft-id task-uuid :task/description)])]
    {:existing? true
     :expanded? true
     :draft (cond-> (from-task task)
              (some? typed-title) (assoc :title typed-title)
              (some? typed-description) (assoc :description typed-description))
     :change! (fn [changes] (>e [::data/save-fields task changes change-fx]))
     :editing-title? @(<s [::lock/held? ident :task/title])
     :editing-details? @(<s [::lock/held? ident :task/description])
     :open-text! (fn [field] (>e [::data/open-text task-uuid field]))
     ;; Synchronous: a render ahead of a queued keystroke would put older
     ;; text back in the box, over what was typed since.
     :type-text! (fn [field value] (>e! [::data/type-text task-uuid field value change-fx]))
     :close-text! (fn [field] (>e [::data/close-text task-uuid field change-fx]))}))

(defn- form-map
  "The host's `props` and the form's `state`, with the fields and what acts on
   them: a new task's draft, or an existing task as it is."
  [state {:keys [task] :as props}]
  (merge props
         state
         (if (:task/uuid task)
           (existing-task-form props)
           (new-task-form state props))))

(defn- holding-place
  "Where a lock is held, as the panel names it — \"Chrome\", \"the extension
   in Safari\" — or nil when the browser couldn't be told."
  [{:keys [browser surface]}]
  (if (= :browser-ext surface)
    (if browser
      (str "the extension in " browser)
      "the extension")
    browser))

(defn- lock-panel-body
  "Who holds the field, since `taken-at`: someone else, who it releases itself
   from after five minutes without typing, or the writer somewhere else — in
   a browser and surface, when known — who `on-take` moves here."
  [{:keys [holder taken-at mine?] :as refusal} on-take on-close]
  [lock-panel
   [:button.lock-panel-close {:type "button" :aria-label "Close" :on-click on-close}
    [icons/outlined-close {:size 13}]]
   [:div.lock-panel-head
    [bubble/assignee-avatar {:assignee holder :me? mine?}]
    (if mine?
      [:span.lock-panel-who
       (if-let [place (holding-place refusal)]
         [:span.lock-panel-sentence
          "You are already editing this field in " [:strong place]]
         [:span.lock-panel-sentence
          "You are already editing this field " [:strong "somewhere else"]])]
      [:span.lock-panel-who
       [:span.lock-panel-name (or (data/assignee-name holder) "Someone")]
       [:span.lock-panel-sub "is editing this field"]])]
   (when taken-at
     [:div.lock-panel-held (str "Held for " (datetime/duration-label taken-at))])
   (if mine?
     [:button.lock-panel-take {:type "button" :on-click on-take} "Edit here instead"]
     [:div.lock-panel-quiet "It will release itself after five minutes without typing."])])

(defn- field-lock
  "The lock panel for an existing task's `field`, open while taking or saving
   the field was refused ([[hai.entity.lock]]). It hangs from an empty
   `.lock-anchor` its field positions: Radix Themes' `Popover.Anchor` drops its
   children, so it can't wrap the field itself."
  [{:keys [existing? task]} field]
  (when existing?
    (let [task-uuid (:task/uuid task)
          ident (data/task-ident task-uuid)
          refusal @(<s [::lock/refusal ident field])
          dismiss! #(>e [::lock/dismiss-refusal ident field])]
      [c/PopoverRoot {:open (some? refusal)
                      :onOpenChange (fn [open?]
                                      (when-not open?
                                        (dismiss!)))}
       [c/PopoverAnchor {:class "lock-anchor"}]
       [c/PopoverContent {:side "bottom" :align "end" :sideOffset 6 :size "1"
                          :onOpenAutoFocus #(.preventDefault %)
                          :onCloseAutoFocus #(.preventDefault %)}
        (when refusal
          [lock-panel-body refusal
           #(>e [::data/open-text task-uuid field {:steal? true}])
           dismiss!])]])))

(defn- text-selected?
  "Whether the click that just landed ended a drag across text, leaving it
   selected: a plain click leaves nothing selected. Read as the selection's
   text, which Chrome also gives inside a shadow root, where it reports every
   selection as collapsed."
  []
  (not (str/blank? (str (js/window.getSelection)))))

(defn- opener
  "What opens a typed field read as text: a click on it, or ↵. A click that
   ends a drag selecting text leaves the field shut, so the text can be
   copied."
  [open!]
  {:title "Click to edit"
   :tab-index 0
   :on-click (fn [_]
               (when-not (text-selected?)
                 (open!)))
   :on-key-down (fn [e]
                  (when (= "Enter" (.-key e))
                    (.preventDefault e)
                    (open!)))})

(defn- title-input
  "The title open, as the field it is typed into. ↵ saves a new task and
   closes an existing task's title, as blur does — though not a blur from
   leaving the page ([[left-the-page?]])."
  [{:keys [existing? draft save! type-text! close-text!]}]
  [:div.title-input-wrap.heading
   [:textarea.title-input
    {:rows 1
     :auto-focus true
     :value (or (:title draft) "")
     :placeholder "What's the task?"
     :on-change #(type-text! :task/title (.. % -target -value))
     :on-focus caret-to-end
     :on-blur (fn [_]
                (when-not (left-the-page?)
                  (close-text! :task/title)))
     :on-key-down (fn [e]
                    (case (.-key e)
                      "Enter" (do (.preventDefault e)
                                  (if existing?
                                    (.. e -target blur)
                                    (save!)))
                      "Escape" (do (.preventDefault e) (.. e -target blur))
                      nil))}]])

(defn- title-text
  [{:keys [draft done? open-text!]}]
  (let [title (:title draft)
        empty? (str/blank? title)]
    [:h2.title-display.editable
     (assoc (opener #(open-text! :task/title))
            :class (cond-> []
                     done? (conj "done")
                     empty? (conj "empty")))
     (if empty? "What's the task?" title)]))

(defn title-field
  "The title, read as text and struck through while `done?`, until it is
   opened: a new task's is open as the form opens, and one of its two typed
   fields at a time; an existing task's opens through its lock."
  [{:keys [editing-title?] :as form}]
  (if editing-title?
    [title-input form]
    [title-text form]))

(defn assignee-bubble
  "The assignee circle: an existing task's opens the picker and reassigns at
   once, a new task's the host's picker."
  [{:keys [existing? task change-fx draft render-assignee-picker change!]}]
  [:span.task-assignee
   (if existing?
     [bubble/component {:task task :change-fx change-fx}]
     [new-task-assignee {:assignee (:assignee draft)
                         :render-assignee-picker render-assignee-picker
                         :on-pick #(change! {:assignee %})}])])

(defn deadline-chip
  "The deadline's chip, whose day and time open the picker."
  [{:keys [draft deadline-controls]}]
  [deadline-picker/chip {:deadline (:deadline draft)
                         :controls deadline-controls}])

(defn deadline-sheet
  "The picker the [[deadline-chip]] opened, as a sheet over the card."
  [{:keys [draft change! deadline-controls]}]
  [deadline-picker/sheet {:controls deadline-controls
                          :deadline (:deadline draft)
                          :on-change #(change! {:deadline %})}])

(defn deadline-popover
  "The deadline's chip, with the picker as a popover off it."
  [{:keys [draft change! deadline-controls]}]
  [deadline-picker/popover {:controls deadline-controls
                            :deadline (:deadline draft)
                            :on-change #(change! {:deadline %})}])

(defn urgency-field
  "The urgency pill, opening the menu that changes it."
  [{:keys [draft change!]}]
  (let [urgency (:urgency draft)]
    [urgency-pill {:class (when-not (urgency/none? urgency) "filled")}
     [urgency-menu/component
      {:value urgency
       :on-change #(change! {:urgency %})
       :heading "Urgency"
       :align "start"}
      [:button {:type "button"
                :aria-label (str "Urgency " (urgency/label urgency) "; change")}
       [urgency-icon]
       [:span (urgency/label urgency)]
       [chevron-icon]]]]))

(defn stamp-slot
  "The host's `stamp`, if given, at the end of the chip row."
  [{:keys [stamp]}]
  (when stamp
    [:div.task-stamp stamp]))

(defn- description-input
  "The description open, as the field it is typed into. ⌘/Ctrl+↵ saves a new
   task and closes an existing task's description, as blur does — though not
   a blur from leaving the page ([[left-the-page?]])."
  [{:keys [existing? draft save! type-text! close-text!]}]
  [:div.detail-input-wrap
   [:textarea.detail-input
    {:auto-focus true
     :value (or (:description draft) "")
     :placeholder "Add a description"
     :on-change #(type-text! :task/description (.. % -target -value))
     :on-focus caret-to-end
     :on-blur (fn [_]
                (when-not (left-the-page?)
                  (close-text! :task/description)))
     :on-key-down (fn [e]
                    (cond
                      (keyboard/submit-shortcut? e)
                      (do (.preventDefault e)
                          (if existing?
                            (.. e -target blur)
                            (save!)))

                      (= "Escape" (.-key e))
                      (do (.preventDefault e) (.. e -target blur))))}]])

(defn- description-text
  [{:keys [draft open-text!]}]
  (let [description (:description draft)
        empty? (str/blank? description)]
    [:div.task-description.editable
     (assoc (opener #(open-text! :task/description))
            :class (when empty? "empty"))
     (if empty? "Add a description" description)]))

(defn description-field
  "The description, read as text until it is opened: a new task's as one of
   its two typed fields at a time, an existing task's through its lock."
  [{:keys [editing-details?] :as form}]
  [:div.task-body
   [field-lock form :task/description]
   (if editing-details?
     [description-input form]
     [description-text form])])

(defn links-row
  "The links row notes renders, each change through `change!`: into a new
   task's draft, or saved at once."
  [{:keys [draft change! !open-sections]}]
  (let [{:keys [urls contacts notes tasks]} draft]
    [sections/links-section
     {:link-urls urls
      :contacts contacts
      :linked-notes notes
      :linked-tasks tasks
      :link-tooltip-text "Attach a contact, note, task or page to this task"
      :editing? true
      :open? (contains? @!open-sections :links)
      :on-open #(swap! !open-sections conj :links)
      :on-cancel #(swap! !open-sections disj :links)
      :on-add (fn [link-url]
                (when-not (some #{link-url} urls)
                  (change! {:urls (conj (vec urls) link-url)})))
      :on-remove (fn [link-url]
                   (change! {:urls (vec (remove #{link-url} urls))}))
      :on-add-contact (fn [contact]
                        (change! {:contacts (conj (vec contacts) contact)}))
      :on-remove-contact (fn [contact]
                           (change! {:contacts (vec (remove #(= (:contact/uuid contact)
                                                                (:contact/uuid %))
                                                            contacts))}))
      :on-add-note (fn [note] (change! {:notes (conj (vec notes) note)}))
      :on-remove-note (fn [note]
                        (change! {:notes (vec (remove #(= (:note/uuid note) (:note/uuid %))
                                                      notes))}))
      :on-add-task (fn [linked-task]
                     (change! {:tasks (conj (vec tasks) linked-task)}))
      :on-remove-task (fn [linked-task]
                        (change! {:tasks (vec (remove #(= (:task/uuid linked-task)
                                                          (:task/uuid %))
                                                      tasks))}))
      :render-entity-search link-combobox/entity-search}]))

(defn tags-row
  "The tags row notes renders, each change through `change!`: into a new
   task's draft, or saved at once."
  [{:keys [draft change! !open-sections spaces? on-open-entity]}]
  (let [{:keys [tags spaces]} draft]
    [sections/tags-section
     {:tags tags
      ;; Space chips always show — the server only resolves spaces the viewer
      ;; can see. `spaces?` (admin + developer-features) gates only the
      ;; add/remove controls (and so the picker offering spaces), which change
      ;; the task as tags do.
      :spaces spaces
      :tag-tooltip-text (if spaces?
                          "Tag this task, or add it to a space"
                          "Tag this task to find it with related work")
      :editing? true
      :open? (contains? @!open-sections :tags)
      :on-open #(swap! !open-sections conj :tags)
      :on-cancel #(swap! !open-sections disj :tags)
      :on-pick (fn [tag] (change! {:tags (conj (vec tags) tag)}))
      :on-remove (fn [tag]
                   (change! {:tags (vec (remove #(= % tag) tags))}))
      :on-add-space (when spaces?
                      (fn [space] (change! {:spaces (conj (vec spaces) space)})))
      :on-remove-space (when spaces?
                         (fn [space]
                           (change! {:spaces (vec (remove #(= (:space/uuid %) (:space/uuid space))
                                                          spaces))})))
      ;; A space chip links to that space, like on notes.
      :on-open-space on-open-entity
      :render-tag-search tag-combobox/search-slot}]))

(defn related-drawer
  "What the task relates to as the drawer along the card's foot, each change
   through `change!`: into a new task's draft, or saved at once."
  [{:keys [existing? task draft draft-id change! spaces? on-open-entity]}]
  (let [drawer-key (drawer-core/drawer-key (when existing? (:task/uuid task)) draft-id)]
    ^{:key drawer-key}
    [drawer/component {:drawer-key drawer-key
                       :task (when existing? task)
                       :draft draft
                       :change! change!
                       :editable? true
                       :spaces? spaces?
                       :on-open-entity on-open-entity}]))

(defn- save-actions
  "A new task's Cancel and Save. Save shows the shortcut that saves from where
   the caret is: ↵ in the title, the chord while the description is open.
   `closes?` as [[entity-form/actions]] has it."
  [{:keys [draft unfiled? saving? show-hint? on-cancel save! editing-details?]} closes?]
  [entity-form/actions
   {:unfiled?       unfiled?
    :saving?        saving?
    :save-disabled? (str/blank? (:title draft))
    :shortcut       (when show-hint?
                      (if editing-details?
                        (entity-form/chord "↵")
                        ["↵"]))
    :closes?        closes?
    :on-cancel      on-cancel
    :on-save        save!}])

(defn footer
  "A new task's Cancel and Save under the form."
  [form]
  [entity-form/footer
   [save-actions form false]])

;; A new task's Cancel and Save on the row of the header above it, without the
;; footer's offset. Cancel is how the form closes there, so it stays enabled
;; with nothing staged.
(o/defstyled header-actions :div
  [entity-form/footer {:margin-top "0"}]
  ([form]
   [entity-form/footer
    [save-actions form true]]))

;; NOTE(evgheni): TODO — stop drilling host values through the task form. Each
;; of these travels from a surface through the mini-app views (or the page),
;; `card-form`/`inline-form` and the form map to the one piece that uses it.
;; Move each where the pieces reach it themselves, as the host page went to
;; app-db (`hai.host-page`) and link matching to shared code (`url.scope`):
;; - `default-urgency`: hosts read `::data/default-urgency` only to hand it
;;   down; the form can subscribe to it.
;; - `render-assignee-picker`: every host passes `hai.tasks.assignee-picker`
;;   with the same props; the form can require it.
;; - `spaces?`: the web-app's `::spaces-data/enabled?` (hai.spaces.data); move
;;   that sub into a shared component and subscribe here.
;; - `on-open-entity`: the web-app's `hai.entity.data/open-entity`; make opening
;;   an entity a shared event each surface handles (the extension opens the
;;   web-app in a tab).
;; - `show-hint?`: whether the device has a keyboard (true on desktop and in the
;;   extension, `(not mobile?)` on the web-app's pages); a shared sub.
;; - `change-fx`: what a host re-reads after a save (the board, the detail
;;   page, the extension's badge); have hosts react to the shared save events.
;; - `task-link` (in `::data/create`'s payload): where a toast links a new task;
;;   a shared route helper each surface provides.
;; - the task row's `:render-extra-actions` (the extension's chain-link) reads
;;   the host page itself now and could become the row's own.
;; The notes and contacts forms and mini-apps carry the same props.
(defn composer
  "The form around a `layout` of its pieces, which puts them in [[shell]]. A
   host's own layout can set pieces beside its own chrome, as the Tasks card's
   header carries a new task's [[header-actions]]. The title and the
   description read as text and are only editors while open, one at a time: a
   click opens one, and a new task opens on its title. Blur or Escape closes
   it. A new task is only its title until something is typed into it or the
   button under it is pressed ([[head]]).

   A new task (no `task`) stages everything in the draft at `draft-id`
   ([[hai.entity.draft]]), which the host seeds, measured against the blank
   task to know whether there is anything to save — a host that seeded it at
   the user's default urgency passes the same value as `default-urgency`, so
   the baseline agrees with the seed. Its bubble opens the host's `render-assignee-picker` slot (the picker needs
   app state) and stages the pick. There is one save, and it only ever fires
   from the shortcuts — ↵ in the title, ⌘/Ctrl+↵ in the description — or the
   Save button, which carries the one that works right now as key caps
   (`show-hint?`; off for touch). It calls `on-save` with the staged fields;
   the host closes the card once the save lands. `saving?` is the host's
   in-flight state — Save disables and spins while it holds, so one save goes
   out at a time. Save and Cancel are always there, disabled while nothing is
   staged — Save also while the title is blank, and Cancel not in a header,
   where it closes the form.

   An existing `task` saves as it is edited, with no Save: a click saves at
   once, and the title and the description save as they are typed, each open
   in one place at a time ([[hai.entity.lock]]). Closing one, or the form
   going away, sends what is left and lets the field go; coming back to the
   page, a field whose lock moved elsewhere meanwhile closes. Its assignee is the
   row's bubble ([[hai.tasks.assignee-bubble]]). `change-fx`
   rides every save: the host's refresh.

   `stamp`, if given, sits at the end of the chip row; `done?` strikes the title
   through while it isn't being edited. `spaces?` turns on the spaces controls
   in the tags row, whose space chips go to `on-open-entity`."
  [{:keys [task change-fx] :as props} layout]
  (r/with-let [state (form-state)
               task-uuid (:task/uuid task)
               ;; Back from another tab, window or the devtools, the fields it
               ;; holds check their locks are still theirs. `focus` and
               ;; `visibilitychange` both fire on the way back; the debounce
               ;; makes them one check.
               recheck! (fn []
                          (when-not (.-hidden js/document)
                            (>e [:debounce/dispatch {:id [::data/recheck-texts task-uuid]
                                                     :event [::data/recheck-texts task-uuid change-fx]
                                                     :duration 150}])))
               _ (when task-uuid
                   (js/window.addEventListener "focus" recheck!)
                   (js/document.addEventListener "visibilitychange" recheck!))]
    [layout (form-map state props)]
    (finally
      (when task-uuid
        (js/window.removeEventListener "focus" recheck!)
        (js/document.removeEventListener "visibilitychange" recheck!)
        (>e [::data/close-texts task-uuid change-fx])))))

(defn- read-only-view
  "The card layout on `task` with nothing to change: the title and the
   description as text, the chips the task carries without menus, and the
   Related drawer without ✕ or the field that adds. The assignee bubble
   opens the picker, and only says why for anyone who may not reassign.
   `stamp`, `done?`, `change-fx` and `on-open-entity` work as in the composer."
  [{:keys [task on-open-entity stamp done? change-fx]}]
  (let [{:keys [title description urgency deadline] :as draft} (from-task task)
        urgency? (not (urgency/none? urgency))]
    [shell
     [:div.task-card-layout
      [:div.task-head
       [:div.task-title-line
        [:h2.title-display {:class (when done? "done")} title]
        [:span.task-assignee
         [bubble/component {:task task :change-fx change-fx}]]]
       (when (or deadline urgency? stamp)
         [:div.task-chip-row
          (when deadline
            [deadline-picker/chip {:deadline deadline}])
          (when urgency?
            [urgency-pill {:class "filled"}
             [:button {:type "button" :disabled true}
              [urgency-icon]
              [:span (urgency/label urgency)]]])
          (when stamp
            [:div.task-stamp stamp])])]
      [:div.task-body
       (if (str/blank? description)
         [:div.task-description.empty "No description."]
         [:div.task-description description])]
      [:div.task-card-foot
       (let [drawer-key (drawer-core/drawer-key (:task/uuid task) nil)]
         ^{:key drawer-key}
         [drawer/component {:drawer-key drawer-key
                            :task task
                            :draft draft
                            :editable? false
                            :on-open-entity on-open-entity}])]]]))

;;
;; The layouts
;;

(defn- head
  "The title with the assignee beside it, over the chips, `deadline` first
   among them. A new task not yet `expanded?` has its title alone, over the
   button that opens the rest. The title stays the first thing in its line
   either way, so it is the same field, caret and all, as the rest arrives."
  [{:keys [expanded? expand!] :as form} deadline]
  [:div.task-head
   [:div.task-title-line
    [title-field form]
    (when expanded?
      [assignee-bubble form])
    (when expanded?
      [field-lock form :task/title])]
   (if expanded?
     [:div.task-chip-row
      deadline
      [urgency-field form]
      [stamp-slot form]]
     [:button.task-expand
      {:type "button"
       :aria-label "Show the rest of the task"
       :title "Show the rest of the task"
       ;; Pressed with the pointer, the title keeps the focus.
       :on-mouse-down #(.preventDefault %)
       :on-click expand!}
      [chevron-icon]])])

(defn card-layout
  "The pieces laid out to fill a fixed-height card: the description takes
   what the Related drawer along the card's foot leaves it, and the
   deadline picker rises over the card as a sheet. A new task's Cancel and
   Save are not among them: the card's header carries them
   ([[header-actions]])."
  [{:keys [expanded?] :as form}]
  [shell
   [:div.task-card-layout
    [head form [deadline-chip form]]
    (when expanded?
      [description-field form])
    (when expanded?
      [:div.task-card-foot
       [related-drawer form]])
    (when expanded?
      [deadline-sheet form])]])

(defn- inline-layout
  "The pieces laid out to grow with their content, the deadline picker
   hanging off its chip as a popover."
  [{:keys [expanded?] :as form}]
  [shell
   [head form [deadline-popover form]]
   (when expanded?
     [description-field form])
   (when expanded?
     [:div.detail-rows
      [links-row form]
      [tags-row form]])
   (when (and expanded? (not (:existing? form)))
     [footer form])])

(defn card-form
  "An existing task's form filling a fixed-height card ([[composer]]'s props)
   — or, with `editing?` false, the same layout read-only ([[read-only-view]]),
   for a viewer who may not change the task."
  [{:keys [editing?] :or {editing? true} :as props}]
  (if editing?
    [composer props card-layout]
    [read-only-view props]))

(defn inline-form
  "The form growing with its content ([[composer]]'s props)."
  [props]
  [composer props inline-layout])
